import React from 'react';
import Icon from '../../../components/AppIcon';

const ValuePreview = ({ selectedTypes }) => {
  const previewBenefits = [
    {
      icon: 'BarChart3',
      title: 'Portfolio Tracking',
      description: 'Monitor your collection value in real-time with market data integration',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'TrendingUp',
      title: 'Market Insights',
      description: 'Get personalized market analysis based on your collection categories',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: 'Shield',
      title: 'Authentication Tools',
      description: 'Access expert guides and verification resources for your collectibles',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: 'Users',
      title: 'Community Access',
      description: 'Connect with fellow collectors and share your passion',
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const sampleCollections = [
    {
      name: 'Vintage Rolex Collection',
      items: 12,
      value: '$145,000',
      growth: '+18.5%',
      image: 'https://images.unsplash.com/photo-1523170335258-f5c6c6bd6eaf?w=400&h=300&fit=crop'
    },
    {
      name: 'Modern Art Portfolio',
      items: 8,
      value: '$89,500',
      growth: '+12.3%',
      image: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=300&fit=crop'
    },
    {
      name: 'Rare Coin Collection',
      items: 45,
      value: '$67,200',
      growth: '+8.7%',
      image: 'https://images.unsplash.com/photo-1621416894569-0f39ed31d247?w=400&h=300&fit=crop'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Message */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-warning rounded-xl flex items-center justify-center liquid-gold">
          <Icon name="Sparkles" size={32} color="#1A1B23" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Welcome to Crazy Rare Club
        </h2>
        <p className="text-muted-foreground">
          You're about to join thousands of collectors who trust us with their most valuable possessions
        </p>
      </div>
      {/* Immediate Benefits */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground text-center">
          What you get immediately:
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {previewBenefits?.map((benefit, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-4 premium-hover transition-all duration-300"
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${benefit?.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={benefit?.icon} size={20} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground mb-1">
                    {benefit?.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {benefit?.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Sample Collections */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground text-center">
          See what other collectors are tracking:
        </h3>
        
        <div className="space-y-3">
          {sampleCollections?.map((collection, index) => (
            <div
              key={index}
              className="bg-surface border border-border rounded-lg p-4 premium-hover transition-all duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={collection?.image}
                    alt={collection?.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">
                    {collection?.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {collection?.items} items
                  </p>
                </div>
                
                <div className="text-right flex-shrink-0">
                  <div className="font-semibold text-foreground">
                    {collection?.value}
                  </div>
                  <div className="text-sm text-success flex items-center">
                    <Icon name="TrendingUp" size={12} className="mr-1" />
                    {collection?.growth}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Trust Indicators */}
      <div className="bg-muted/30 rounded-lg p-6 text-center">
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-2xl font-bold text-primary">50K+</div>
            <div className="text-xs text-muted-foreground">Active Collectors</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary">$2.1B</div>
            <div className="text-xs text-muted-foreground">Assets Tracked</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary">99.9%</div>
            <div className="text-xs text-muted-foreground">Uptime</div>
          </div>
        </div>
        
        <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Shield" size={14} className="text-success" />
            <span>Bank-level Security</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Award" size={14} className="text-success" />
            <span>Industry Trusted</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={14} className="text-success" />
            <span>24/7 Support</span>
          </div>
        </div>
      </div>
      {/* Selected Types Preview */}
      {selectedTypes?.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Check" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              Your personalized experience is ready!
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            We've customized your dashboard based on your selected collectible types. You'll see relevant market data, news, and community content right away.
          </p>
        </div>
      )}
    </div>
  );
};

export default ValuePreview;